import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { PlayCircleIcon } from "@heroicons/react/24/solid";
import Button from "@/components/ui/Button";
import VideoModal from "@/components/ui/VideoModal";

interface Service {
  id: string;
  videoId: string;
  petImage: string;
  serviceImage: string;
  comingSoon?: boolean;
}

const servicesData: Service[] = [
  {
    id: "card1",
    videoId: "TMry8zNtGLA",
    petImage: "/images/logo/NALAaiPet(1).webp",
    serviceImage: "/images/services/NALAAI.webp",
  },
  {
    id: "card2",
    videoId: "6pfV2TtsBko",
    petImage: "/images/logo/SOAPvettPet(1).webp",
    serviceImage: "/images/services/SOAPvett.webp",
  },
  {
    id: "card3",
    videoId: "nwjSrRDizi4",
    petImage: "/images/logo/ClinicFlowPet.webp",
    serviceImage: "/images/services/ClinicFlow.webp",
  },
  {
    id: "card4",
    videoId: "iNxMWge3ttQ",
    petImage: "/images/logo/WooflyPet(1).webp",
    serviceImage: "/images/services/vetTALK.webp",
  },
  {
    id: "card5",
    videoId: "N2IRGq1QdKY",
    petImage: "/images/logo/vettBOXPet.webp",
    serviceImage: "/images/services/vettBOX.webp",
  },
  {
    id: "card6",
    videoId: "",
    petImage: "/images/logo/VettConsultPet.png",
    serviceImage: "/images/services/VideoConsultingImage.webp",
    comingSoon: true,
  },
];

const ModernServicesSection: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const langPrefix = `/${i18n.language}`;
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);

  const handleLearnMore = (videoId: string, comingSoon?: boolean) => {
    if (comingSoon) return;
    if (videoId) setSelectedVideoId(videoId);
  };

  return (
    <section className="py-24 relative !overflow-visible">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-30">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-4xl sm:text-5xl font-bold text-[#00342d] mb-6 tracking-tight"
            style={{ fontFamily: "'Bruno Ace', sans-serif" }}
          >
            {t("home.services.title", "Our Services")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-xl text-gray-600 leading-relaxed"
          >
            {t(
              "home.services.subtitle",
              "Comprehensive solutions to enhance your veterinary practice."
            )}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16 !overflow-visible p-4">
          {servicesData.map((service, index) => (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="group bg-white rounded-3xl border border-gray-100 shadow-lg overflow-hidden hover:shadow-xl hover:border-[#00e9be]/30 transition-shadow transition-colors duration-300 flex flex-col"
            >
              {/* Image Container */}
              <div className="relative h-56 overflow-hidden bg-gray-50">
                {service.comingSoon ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                    <span className="text-2xl font-bold text-gray-400">
                      Coming Soon
                    </span>
                  </div>
                ) : (
                  <>
                    <img
                      src={service.serviceImage}
                      alt={t(`home.services.${service.id}.title`)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300" />

                    {/* Play Button Overlay */}
                    <button
                      onClick={() =>
                        handleLearnMore(service.videoId, service.comingSoon)
                      }
                      className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    >
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                        <PlayCircleIcon className="w-10 h-10 text-[#00e9be]" />
                      </div>
                    </button>
                  </>
                )}

                {/* Pet Icon Badge */}
                <div className="absolute bottom-4 left-4 w-12 h-12 bg-white rounded-xl shadow-md p-2 flex items-center justify-center">
                  <img
                    src={service.petImage}
                    alt="Icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="p-8 flex flex-col flex-grow">
                <h3 className="text-xl font-bold text-[#00342d] mb-3 group-hover:text-[#00e9be] transition-colors duration-300">
                  {t(
                    `home.services.${service.id}.title`,
                    `Service ${index + 1}`
                  )}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-6 flex-grow">
                  {t(
                    `home.services.${service.id}.description`,
                    "Service description"
                  )}
                </p>
                <button
                  onClick={() =>
                    handleLearnMore(service.videoId, service.comingSoon)
                  }
                  className="text-[#00e9be] font-semibold hover:text-[#00d1aa] transition-colors flex items-center gap-2 self-start"
                >
                  {t("home.services.learnMore", "Learn More")}
                  <span className="text-lg">→</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="text-center relative z-20 py-4">
          <Button
            onClick={() => navigate(`${langPrefix}/services`)}
            className="text-lg px-10 py-4 font-bold tracking-wide shadow-lg hover:shadow-xl hover:scale-105 transition-transform transition-shadow duration-300 text-[#00342d]"
          >
            {t("home.services.viewAllButton", "View All Services")}
          </Button>
        </div>
      </div>

      {selectedVideoId && (
        <VideoModal
          videoId={selectedVideoId}
          isOpen={!!selectedVideoId}
          onClose={() => setSelectedVideoId(null)}
          title={t("home.services.videoTitle", "Service Video")}
        />
      )}
    </section>
  );
};

export default ModernServicesSection;
